import { useEffect, useMemo, useState } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { Button } from '../../components/ui/Button';
import { fetchSystemStats, type PipelineStats } from '../../api/dataApi';
import { useStore } from '../../store/StoreContext';
import { useCueDiagnostics } from '../../lib/cue/useCueDiagnostics';
import {
  cueConnectionStatusMeta,
  describeCueEvent,
  eventTypeLabel,
  resetCueCounters,
  type CueConnectionState,
  type CueCounters,
  type CueDiagnosticEvent,
} from '../../lib/cue/diagnostics';

// Live visibility into the Cue signaling connection (WebSocket - see
// lib/cue/client.ts's header comment) and outstanding REST requests -
// reachable from Settings, and from the small connection badge
// AppLayout shows whenever logged in (see that layout's own comment).
// Everything here reads from the shared lib/cue/diagnostics.ts store
// (via useCueDiagnostics) and TanStack Query's own useIsFetching, which
// client.ts/queryClient.ts already push into - this screen never touches
// a CueClient or the fetch layer directly.
export function DiagnosticsScreen() {
  const { connectionState, lastHeartbeatAt, lastHeartbeatRttMs, events, counters } = useCueDiagnostics();
  const { profile } = useStore();
  // Server-side Cue pipeline throughput (GET /stats/system) - the
  // counterpart to the client-side "Traffic totals" above. Internal
  // plumbing, so it's only fetched/shown when a ZMAP super-admin has set
  // the per-account `diagnosticsServerPipeline` flag (profileApi maps it
  // to profile.showServerPipeline).
  const [pipeline, setPipeline] = useState<PipelineStats | null>(null);
  useEffect(() => {
    if (!profile.showServerPipeline) return;
    let cancelled = false;
    fetchSystemStats().then((s) => !cancelled && setPipeline(s.pipeline));
    return () => {
      cancelled = true;
    };
  }, [profile.showServerPipeline]);
  // Same three key families StoreContext.tsx's isSyncing counts (see its
  // own comment) - this screen's "in flight" number should match what
  // the app-wide sync bar is reacting to, not diverge from it.
  const inFlight =
    useIsFetching({ queryKey: ['channels'] }) + useIsFetching({ queryKey: ['templates'] }) + useIsFetching({ queryKey: ['channelMessages'] });

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Diagnostics" />

      <FormSection title="Cue connection">
        <div className="flex items-center justify-between">
          <span className="text-[15px] font-medium">Status</span>
          <ConnectionStatusPill state={connectionState} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[15px] font-medium">Last heartbeat</span>
          <span className="text-sm text-black/50 dark:text-white/50 tabular-nums">
            {heartbeatSummary(lastHeartbeatAt, lastHeartbeatRttMs)}
          </span>
        </div>
      </FormSection>

      <FormSection title="REST requests">
        <div className="flex items-center justify-between">
          <span className="text-[15px] font-medium">In flight</span>
          <span className="text-sm text-black/50 dark:text-white/50">{inFlight}</span>
        </div>
      </FormSection>

      <TrafficTotals counters={counters} />

      {pipeline && <ServerPipeline pipeline={pipeline} />}

      <TrafficLog events={events} />
    </div>
  );
}

// The In/Out event log. Shows about five rows and then scrolls (the full
// history is rarely what you want - the last few sends/replies usually
// answer "is it working right now?"), with an optional "Group by type"
// view that buckets everything by eventTypeLabel (Heartbeats, Joins,
// Messages, Relays, Replies, Timeouts, Errors, ...).
function TrafficLog({ events }: { events: CueDiagnosticEvent[] }) {
  const [grouped, setGrouped] = useState(false);

  // Newest first - the most recent heartbeat/relay/reply is what you're
  // actually here to check.
  const newestFirst = useMemo(() => [...events].reverse(), [events]);
  const groups = useMemo(() => {
    if (!grouped) return [];
    const byType = new Map<string, CueDiagnosticEvent[]>();
    for (const event of newestFirst) {
      const key = eventTypeLabel(event);
      const list = byType.get(key);
      if (list) list.push(event);
      else byType.set(key, [event]);
    }
    // Biggest buckets first.
    return [...byType.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [grouped, newestFirst]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between px-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">In / out traffic</span>
        {events.length > 0 && (
          <Button variant="bordered" onClick={() => setGrouped((v) => !v)} className="text-xs px-2.5 py-1">
            {grouped ? 'Ungroup' : 'Group by type'}
          </Button>
        )}
      </div>
      {/* A bespoke container, not AppCard - AppCard's own p-4/p-5 default
          can't be reliably zeroed out via a caller className, and every
          row here needs its own padding/divider instead of one shared
          card inset. max-h ≈ five EventRows before it scrolls. */}
      <div className="w-full rounded-2xl border border-black/[0.06] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.06] backdrop-blur-xl overflow-hidden">
        {events.length === 0 ? (
          <div className="p-4 text-sm text-black/40 dark:text-white/40">No Cue traffic yet.</div>
        ) : (
          <div className="max-h-[20rem] overflow-y-auto divide-y divide-black/[0.06] dark:divide-white/[0.08]">
            {grouped
              ? groups.map(([type, rows]) => (
                  <div key={type}>
                    <div className="sticky top-0 z-10 flex items-center justify-between bg-black/[0.04] dark:bg-white/[0.08] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-black/50 dark:text-white/50 backdrop-blur">
                      <span>{type}</span>
                      <span className="tabular-nums">{rows.length}</span>
                    </div>
                    {rows.map((event) => (
                      <EventRow key={event.id} event={event} />
                    ))}
                  </div>
                ))
              : newestFirst.map((event) => <EventRow key={event.id} event={event} />)}
          </div>
        )}
      </div>
      <span className="text-xs text-black/40 dark:text-white/40 px-1">
        Every session heartbeat, join, send, reply, and relay this tab has sent or received, most recent first. Capped at the last 100 events.
      </span>
    </div>
  );
}

// Lifetime totals for this tab's Cue connection - counts keep going past
// the 100-event ring buffer below (see diagnostics.ts's CueCounters).
function TrafficTotals({ counters }: { counters: CueCounters }) {
  const channels = Object.entries(counters.byChannel).sort((a, b) => b[1].in + b[1].out - (a[1].in + a[1].out));
  const heartbeatMisses = counters.heartbeatsSent - counters.heartbeatsAcked;
  return (
    <FormSection title="Traffic totals">
      <CountRow label="Messages / signals sent" value={counters.messagesSent} />
      <CountRow label="Messages / signals received" value={counters.messagesReceived} />
      <CountRow label="Round controls sent" value={counters.roundsSent} />
      <CountRow label="Channel joins sent" value={counters.joinsSent} />
      <CountRow
        label="Heartbeats (sent / acked)"
        value={`${counters.heartbeatsSent} / ${counters.heartbeatsAcked}${heartbeatMisses > 0 ? ` — ${heartbeatMisses} unanswered` : ''}`}
      />
      <CountRow label="Timeouts" value={counters.timeouts} />
      <CountRow label="Errors" value={counters.errors} />

      {channels.length > 0 && (
        <div className="flex flex-col gap-1.5 pt-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40">Per channel (in / out)</span>
          {channels.map(([channelId, t]) => (
            <div key={channelId} className="flex items-center justify-between text-sm">
              <span className="truncate text-black/70 dark:text-white/70">{channelId}</span>
              <span className="tabular-nums text-black/50 dark:text-white/50">
                ↓{t.in} ↑{t.out}
              </span>
            </div>
          ))}
        </div>
      )}

      <Button variant="bordered" onClick={resetCueCounters} className="mt-1 self-start text-sm">
        Reset totals
      </Button>
    </FormSection>
  );
}

function CountRow({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[15px] font-medium">{label}</span>
      <span className="text-sm text-black/50 dark:text-white/50 tabular-nums">{value}</span>
    </div>
  );
}

// Server-side Cue pipeline throughput: every message flows
// receive -> triage -> storage -> broadcast. Lifetime counters, shared
// across every worker replica (see backend/protocol/metrics.go).
function ServerPipeline({ pipeline }: { pipeline: PipelineStats }) {
  const errs = pipeline.triageErrors + pipeline.storageErrors + pipeline.broadcastErrors;
  return (
    <FormSection title="Server pipeline">
      <CountRow label="Received" value={pipeline.received.toLocaleString()} />
      <CountRow label="Triaged" value={pipeline.triaged.toLocaleString()} />
      <CountRow label="Persisted (storage)" value={pipeline.persisted.toLocaleString()} />
      <CountRow label="Broadcast" value={pipeline.broadcast.toLocaleString()} />
      <CountRow label="Relay deliveries" value={pipeline.delivered.toLocaleString()} />
      <CountRow
        label="Stage errors (triage / storage / broadcast)"
        value={`${pipeline.triageErrors} / ${pipeline.storageErrors} / ${pipeline.broadcastErrors}${errs > 0 ? ` — ${errs} total` : ''}`}
      />
      {pipeline.since > 0 && (
        <span className="text-xs text-black/40 dark:text-white/40">Counting since {new Date(pipeline.since * 1000).toLocaleString()}.</span>
      )}
    </FormSection>
  );
}

function heartbeatSummary(lastHeartbeatAt: number | null, rttMs: number | null): string {
  if (lastHeartbeatAt === null) return 'never';
  const ageSec = Math.round((Date.now() - lastHeartbeatAt) / 1000);
  const rtt = rttMs !== null ? ` (${rttMs}ms)` : '';
  return ageSec < 2 ? `just now${rtt}` : `${ageSec}s ago${rtt}`;
}

function ConnectionStatusPill({ state }: { state: CueConnectionState }) {
  const { color, label } = cueConnectionStatusMeta(state);
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-black/50 dark:text-white/50">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {label}
    </span>
  );
}

function EventRow({ event }: { event: CueDiagnosticEvent }) {
  const isError = event.kind === 'error' || event.kind === 'timeout';
  const arrowColor = isError ? 'text-red-500' : event.direction === 'out' ? 'text-blue-500' : 'text-emerald-500';
  return (
    <div className="flex items-start gap-2.5 px-4 py-2.5">
      <span className={`mt-0.5 text-xs font-bold ${arrowColor}`}>{event.direction === 'out' ? '↑' : '↓'}</span>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="text-[13px] break-words">{describeCueEvent(event)}</span>
        {/* Raw wire shorthand, kept as a small secondary line for anyone
            debugging at the protocol level - describeCueEvent's sentence
            above is the primary reading experience now. */}
        <span className="text-[11px] font-mono text-black/40 dark:text-white/40 break-all">
          {event.command ?? event.kind}
          {event.channelId ? ` ${event.channelId}` : ''}
        </span>
        <span className="text-[11px] text-black/40 dark:text-white/40">{new Date(event.timestamp).toLocaleTimeString()}</span>
      </div>
    </div>
  );
}
